import { useState } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { Banner, Button, EmptyState, Tabs } from '@immo/ui-kit';
import { FLEET_METRICS_TIME_ZONE, type FleetMetricAggregate, type FleetMetricsSeries } from '@immo/api-contract';
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useRevalidator,
  type LoaderFunctionArgs,
} from 'react-router-dom';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { ChartIcon } from '@/components/layout/icons';
import {
  HeatCard,
  KpiRow,
  LeaderCard,
  MetricsFooter,
  MetricsSkeleton,
  MixCard,
  SplitCard,
  type LeaderField,
  type MixField,
  type SplitField,
} from '@/components/fleet/metric-cards';
import { AgentFilter, ClassPicker, isoWeekday, PeriodPicker } from '@/components/fleet/metric-filters';
import { PanelsCard, type BucketInfo, type PanelData, type SeriesRow } from '@/components/fleet/metric-panels';
import {
  costBy,
  keyOf,
  METRICS_VIEWS,
  VIEW_GROUPING,
  type ClassChoice,
  type MetricsView,
} from '@/components/fleet/metrics-math';
import {
  civilToday,
  currentPeriod,
  hasNext,
  hasPrevious,
  isoWeekKey,
  PERIOD_BUCKET,
  periodFromSearch,
  periodWindow,
  shiftPeriod,
  type Period,
  type PeriodScale,
} from '@/components/fleet/period';
import { isPriced, TOKEN_CLASSES } from '@/components/fleet/pricing';
import type { SortState } from '@/components/sortable-table';

export type MetricsState = {
  readonly view: MetricsView;
  readonly period: Period;
  /** `null` = every agent, the default. */
  readonly agents: ReadonlyArray<string> | null;
  readonly choice: ClassChoice;
};

export type FleetMetricsLoaderData =
  | {
      readonly status: 'ok';
      readonly state: MetricsState;
      readonly series: FleetMetricsSeries;
      /** Profile × model over the period, unfiltered: the agent list, its run counts, and euro per model. */
      readonly mix: FleetMetricsSeries;
    }
  | { readonly status: 'unreachable'; readonly state: MetricsState };

/** A refused read is the route's error (403 → access denied); any other failure is the backend being out of reach. */
class Unreachable extends Error {}

export function stateFromSearch(params: URLSearchParams, now: Date = new Date()): MetricsState {
  const view = params.get('view');
  const choice = params.get('class');
  const agents = (params.get('agents') ?? '')
    .split(',')
    .map((agent) => agent.trim())
    .filter((agent) => agent !== '');

  return {
    view: METRICS_VIEWS.includes(view as MetricsView) ? (view as MetricsView) : 'machine',
    period: periodFromSearch(params, now),
    agents: agents.length > 0 ? agents : null,
    choice: TOKEN_CLASSES.includes(choice as (typeof TOKEN_CLASSES)[number]) ? (choice as ClassChoice) : 'all',
  };
}

export function searchFromState(state: MetricsState): string {
  const params = new URLSearchParams();

  params.set('view', state.view);
  params.set('scale', state.period.scale);
  params.set('at', state.period.key);

  if (state.agents !== null) params.set('agents', state.agents.join(','));

  if (state.choice !== 'all') params.set('class', state.choice);

  return `?${params.toString()}`;
}

type SeriesQuery = Parameters<typeof apiClient.adminFleetMetrics.series>[0] extends { query: infer Q } ? Q : never;

async function readSeries(query: SeriesQuery): Promise<FleetMetricsSeries> {
  let result: Awaited<ReturnType<typeof apiClient.adminFleetMetrics.series>>;

  try {
    result = await apiClient.adminFleetMetrics.series({ query });
  } catch {
    throw new Unreachable();
  }

  if (result.ok) return result.body;

  if (result.status === 401 || result.status === 403) {
    throw new Response('Fleet metrics refused', { status: result.status });
  }

  throw new Unreachable();
}

export async function fleetMetricsLoader({ request }: LoaderFunctionArgs): Promise<FleetMetricsLoaderData> {
  const state = stateFromSearch(new URL(request.url).searchParams);
  const window = periodWindow(state.period);
  const range = { from: window.from.toISOString(), to: window.to.toISOString() };

  try {
    // Both reads are needed for one honest picture: a failed one is the
    // backend out of reach, never an empty agent list (BL-058).
    const [series, mix] = await Promise.all([
      readSeries({
        ...range,
        bucket: PERIOD_BUCKET[state.period.scale],
        groupBy: [...VIEW_GROUPING[state.view]],
        ...(state.agents !== null ? { profile: state.agents } : {}),
        previousFrom: periodWindow(shiftPeriod(state.period, -1)).from.toISOString(),
        heatmap: state.view === 'machine' && state.period.scale !== 'day' ? 'true' : 'false',
      }),
      readSeries({ ...range, bucket: 'month', groupBy: ['profile', 'model'], heatmap: 'false' }),
    ]);

    return { status: 'ok', state, series, mix };
  } catch (error) {
    if (error instanceof Unreachable) return { status: 'unreachable', state };
    throw error;
  }
}

const VIEW_LABEL: Readonly<Record<MetricsView, string>> = {
  machine: 'view-machine',
  agent: 'view-agent',
  model: 'view-model',
};

const VERSUS: Readonly<Record<PeriodScale, string>> = {
  day: 'versus-day',
  week: 'versus-week',
  month: 'versus-month',
  thirteenWeeks: 'versus-thirteen-weeks',
};

function berlinHour(instant: Date): number {
  return Number(
    new Intl.DateTimeFormat('en-GB', { timeZone: FLEET_METRICS_TIME_ZONE, hour: '2-digit', hourCycle: 'h23' }).format(
      instant,
    ),
  );
}

/** Axis labels and tooltip titles for each bucket, on the Berlin calendar. */
export function bucketInfos(
  buckets: FleetMetricsSeries['buckets'],
  scale: PeriodScale,
  t: (key: string, values?: Record<string, string | number>) => string,
  intl: IntlShape,
): BucketInfo[] {
  const short = (civil: Date) => intl.formatDate(civil, { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
  const long = (civil: Date) =>
    intl.formatDate(civil, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });

  return buckets.map((bucket, index) => {
    const start = new Date(bucket.start);
    const civil = civilToday(start);
    const weekday = isoWeekday(civil);
    const dayTitle = t('label-day', { weekday: t(`weekday-${weekday}`), date: long(civil) });

    switch (scale) {
      case 'day': {
        const hour = String(berlinHour(start)).padStart(2, '0');

        return {
          label: hour,
          sub: Number(hour) % 6 === 0 ? `${hour}:00` : '',
          tip: `${dayTitle} · ${hour}:00`,
          weekend: false,
        };
      }

      case 'week':
        return { label: t(`weekday-${weekday}`), sub: short(civil), tip: dayTitle, weekend: weekday >= 6 };
      case 'month':
        return {
          label: String(civil.getUTCDate()),
          sub: weekday === 1 ? t('weekday-1') : '',
          tip: dayTitle,
          weekend: weekday >= 6,
        };
      case 'thirteenWeeks': {
        const week = Number(isoWeekKey(civil).slice(-2));
        const last = civilToday(new Date(new Date(bucket.end).getTime() - 1));

        return {
          label: String(week),
          sub: index % 2 === 0 ? short(civil) : '',
          tip: `${t('label-week', { week })} · ${t('range', { from: short(civil), to: long(last) })}`,
          weekend: false,
        };
      }
    }
  });
}

function toRow(group: FleetMetricsSeries['groups'][number], dimension: 'host' | 'profile' | 'model'): SeriesRow {
  return { key: keyOf(group, dimension), total: group.total, previous: group.previous, series: group.series };
}

function panelsOf(series: FleetMetricsSeries, view: MetricsView): PanelData[] {
  const [panelDimension, rowDimension] = VIEW_GROUPING[view];

  return series.panels.map((panel) => ({
    ...toRow(panel, panelDimension),
    rows: series.groups
      .filter((group) => keyOf(group, panelDimension) === keyOf(panel, panelDimension))
      .map((group) => toRow(group, rowDimension)),
  }));
}

function mixCells(series: FleetMetricsSeries): Map<string, Map<string, FleetMetricAggregate>> {
  const cells = new Map<string, Map<string, FleetMetricAggregate>>();

  for (const group of series.groups) {
    const profile = keyOf(group, 'profile');
    const byModel = cells.get(profile) ?? new Map<string, FleetMetricAggregate>();

    byModel.set(keyOf(group, 'model'), group.total);
    cells.set(profile, byModel);
  }

  return new Map([...cells.entries()].sort(([a], [b]) => a.localeCompare(b)));
}

function UnreachableBanner({ onRetry }: { readonly onRetry: () => void }) {
  const t = useTranslations('pages.admin.fleet-metrics');

  // The only red state on this surface: here, something is actually broken.
  return (
    <Banner
      variant="danger"
      title={t('unreachable-title')}
      description={t('unreachable-body')}
      actions={
        <Button size="sm" variant="secondary" onClick={onRetry}>
          {t('retry')}
        </Button>
      }
    />
  );
}

/**
 * The fleet's token usage (IMM-911 Metriken, design run
 * `.docs/design/features/imm-911-fleet-token-metrics/`). Three views that
 * differ only in grouping, one scale per row set, the four classes always
 * reachable, euro a side figure computed here from the dated price table.
 */
export function FleetMetricsPage() {
  const t = useTranslations('pages.admin.fleet-metrics');
  const intl = useIntl();
  const data = useLoaderData() as FleetMetricsLoaderData;
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const compact = useMediaQuery(PHONE_QUERY);
  // Held here, not in the cards: every filter change unmounts them under the loading skeleton.
  const splitSort = useState<SortState<SplitField>>(null);
  const leaderSort = useState<SortState<LeaderField>>(null);
  const mixSort = useState<SortState<MixField>>(null);
  const location = useLocation();
  const navigation = useNavigation();
  // A filter change is a navigation whose loader takes a moment: the controls
  // show the choice at once and the body turns to a skeleton until it lands.
  const pendingSearch =
    navigation.state === 'loading' && navigation.location.pathname === location.pathname
      ? navigation.location.search
      : null;
  const loading = pendingSearch !== null || revalidator.state === 'loading';
  const state = pendingSearch !== null ? stateFromSearch(new URLSearchParams(pendingSearch)) : data.state;
  const go = (next: Partial<MetricsState>) => navigate({ search: searchFromState({ ...state, ...next }) });
  const firstRunAt = data.status === 'ok' && data.series.firstRunAt !== null ? new Date(data.series.firstRunAt) : null;

  const agentsInPeriod = (() => {
    if (data.status !== 'ok') return { list: [] as string[], runs: new Map<string, number>() };

    const runs = new Map<string, number>();

    for (const group of data.mix.groups) {
      const profile = keyOf(group, 'profile');

      runs.set(profile, (runs.get(profile) ?? 0) + group.total.runs);
    }

    const list = [...new Set([...runs.keys(), ...(state.agents ?? [])])].sort();

    return { list, runs };
  })();

  const selectedAgents = state.agents ?? agentsInPeriod.list;
  const onAgents = (next: ReadonlyArray<string>) =>
    go({ agents: agentsInPeriod.list.every((agent) => next.includes(agent)) ? null : next });

  const filters = (
    <div
      className="flex min-w-0 flex-col"
      style={{
        gap: 'var(--space-2)',
        padding: compact ? 'var(--space-3) var(--space-4)' : 'var(--space-3) var(--space-6)',
        background: 'var(--bg-surface)',
        borderBottom: '1px solid var(--color-neutral-200)',
      }}
    >
      <div className="flex min-w-0 items-center">
        {!compact && (
          <span className="flex-none text-sm text-text-tertiary" style={{ width: 64 }}>
            {t('filter-period')}
          </span>
        )}
        <PeriodPicker
          period={state.period}
          canPrevious={hasPrevious(state.period, firstRunAt)}
          canNext={hasNext(state.period)}
          compact={compact}
          onScale={(scale) => go({ period: currentPeriod(scale) })}
          onStep={(steps) => go({ period: shiftPeriod(state.period, steps) })}
        />
      </div>
      <div
        className="flex min-w-0 flex-wrap items-center"
        style={{ gap: compact ? 'var(--space-2)' : 'var(--space-6)' }}
      >
        <span className="inline-flex items-center">
          {!compact && (
            <span className="flex-none text-sm text-text-tertiary" style={{ width: 64 }}>
              {t('filter-agents')}
            </span>
          )}
          <AgentFilter
            agents={agentsInPeriod.list}
            selected={selectedAgents}
            runsByAgent={agentsInPeriod.runs}
            onChange={onAgents}
          />
        </span>
        <span className="inline-flex min-w-0 flex-wrap items-center" style={{ gap: 'var(--space-3)' }}>
          {!compact && <span className="text-sm text-text-tertiary">{t('filter-class')}</span>}
          <ClassPicker value={state.choice} onChange={(choice) => go({ choice })} />
        </span>
      </div>
    </div>
  );

  const body = (() => {
    if (loading) return <MetricsSkeleton compact={compact} />;

    if (data.status === 'unreachable') return <UnreachableBanner onRetry={() => revalidator.revalidate()} />;

    const { series, mix } = data;

    if (series.total.runs === 0) {
      const window = periodWindow(state.period);
      const beforeLedger = firstRunAt !== null && firstRunAt.getTime() >= window.to.getTime();
      const description =
        firstRunAt === null
          ? t('empty-body-ledger')
          : beforeLedger
            ? t('empty-body', {
                date: intl.formatDate(civilToday(firstRunAt), {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  timeZone: 'UTC',
                }),
              })
            : undefined;

      return (
        <div className="flex min-h-[320px] flex-col items-center justify-center" style={{ gap: 'var(--space-3)' }}>
          <EmptyState
            icon={<ChartIcon className="h-8 w-8" aria-hidden />}
            title={t('empty-title')}
            {...(description !== undefined ? { description } : {})}
          />
          <Button variant="secondary" size="sm" onClick={() => go({ period: currentPeriod(state.period.scale) })}>
            {t('empty-cta')}
          </Button>
        </div>
      );
    }

    const [panelDimension, rowDimension] = VIEW_GROUPING[state.view];
    const panels = panelsOf(series, state.view);
    const buckets = bucketInfos(series.buckets, state.period.scale, t, intl);
    const previousLabel = series.previous === null ? null : t(VERSUS[state.period.scale]);
    const expectedRows = [...new Set(series.groups.map((group) => keyOf(group, rowDimension)))].sort();
    const selectedMix = mix.groups.filter((group) => selectedAgents.includes(keyOf(group, 'profile')));
    const costs = costBy(selectedMix, 'profile');
    const totalCost = [...costs.values()].reduce((sum, cost) => sum + cost, 0);
    const unpriced = [...new Set(selectedMix.map((group) => keyOf(group, 'model')))].filter(
      (model) => !isPriced(model),
    );

    return (
      <>
        <KpiRow
          total={series.total}
          previous={series.previous}
          choice={state.choice}
          cost={totalCost}
          previousLabel={previousLabel ?? ''}
          compact={compact}
        />
        <PanelsCard
          view={state.view}
          panels={panels}
          buckets={buckets}
          choice={state.choice}
          total={series.total}
          expectedRows={expectedRows}
          previousLabel={previousLabel}
          compact={compact}
        />
        {state.view === 'machine' && series.heatmap !== undefined && series.heatmap.length > 0 && (
          <HeatCard heatmap={series.heatmap} choice={state.choice} compact={compact} />
        )}
        {state.view === 'agent' && (
          <LeaderCard
            rows={panels}
            choice={state.choice}
            costs={costs}
            hasPrevious={series.previous !== null}
            sortState={leaderSort}
          />
        )}
        {state.view === 'model' && (
          <MixCard
            models={series.panels.map((panel) => keyOf(panel, panelDimension))}
            cells={mixCells(series)}
            choice={state.choice}
            sortState={mixSort}
          />
        )}
        {!compact && <SplitCard view={state.view} rows={panels} sortState={splitSort} />}
        <MetricsFooter total={series.total} unpricedModels={unpriced} />
      </>
    );
  })();

  return (
    <>
      <PageTopbar
        leading={
          <ListLeading title={t('title')} count={data.status === 'ok' ? data.series.total.runs : 0} icon={ChartIcon} />
        }
      />
      <div className="flex h-full min-h-0 flex-col overflow-y-auto">
        <div
          className="flex-none"
          style={{
            padding: compact ? 'var(--space-4) var(--space-4) 0' : 'var(--space-5) var(--space-6) 0',
            background: 'var(--bg-surface)',
          }}
        >
          <p className="text-sm text-text-secondary" style={{ margin: '0 0 var(--space-3)' }}>
            {t('subtitle')}
          </p>
          <Tabs<MetricsView>
            idBase="fleet-metrics-view"
            tabs={METRICS_VIEWS.map((view) => ({ key: view, label: t(VIEW_LABEL[view]) }))}
            active={state.view}
            onChange={(view) => go({ view })}
          />
        </div>
        <div className="flex-none">{filters}</div>
        {/* Children keep their height: a shrinking card clips its content silently. */}
        <div
          className="flex w-full min-w-0 flex-col [&>*]:flex-none"
          style={{ gap: 'var(--space-4)', padding: compact ? 'var(--space-4)' : 'var(--space-5) var(--space-6)' }}
        >
          {body}
        </div>
      </div>
    </>
  );
}
